import * as React from "react";
import { useEffect, useState } from "react";
import GlobalStyles from "@mui/joy/GlobalStyles";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Chip from "@mui/joy/Chip";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import Grid from "@mui/joy/Grid";
import Alert from "@mui/joy/Alert";
import CircularProgress from "@mui/joy/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../utils/FireStore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, addDoc, onSnapshot, getDocs } from 'firebase/firestore';
import settings from "../../utils/settings.json";
import FenceLinkNameLogo from "../../assets/icons/name-logo/Component 1.svg";
  
export default function SubscribeNowPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Redirect to sign-in if not authenticated
    if (!loading && !user) {
      navigate("/sign-in");
      return;
    }

    // Fetch products and prices directly from Firestore
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        
        // Fetch all products from the 'subscriptions' collection
        const productsRef = collection(db, 'subscriptions');
        const productsSnapshot = await getDocs(productsRef);
        
        const productsData = [];
        
        for (const productDoc of productsSnapshot.docs) {
          const productData = { id: productDoc.id, ...productDoc.data() };
          
          // Fetch prices for this product
          const pricesRef = collection(db, 'subscriptions', productDoc.id, 'prices');
          const pricesSnapshot = await getDocs(pricesRef);
          
          const prices = [];
          pricesSnapshot.forEach((priceDoc) => {
            prices.push({ id: priceDoc.id, ...priceDoc.data() });
          });
          
          productData.prices = prices;
          
          // Only include active products
          if (productData.active && !productData.metadata.isTest) {
            productsData.push(productData);
          }
        }

        // Sort products by price (assuming monthly pricing)
        const sortedProducts = productsData.sort((a, b) => {
          const priceA = a.prices?.[0]?.unit_amount || 0;
          const priceB = b.prices?.[0]?.unit_amount || 0;
          return priceA - priceB;
        });

        setProducts(sortedProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
        setErrorMessage("Failed to load subscription plans. Please try again.");
      } finally {
        setLoadingProducts(false);
      }
    };

    if (user) {
      fetchProducts();
    }
  }, [user, loading, navigate]);

  const handleSubscribe = async (priceId) => {
    if (!user) return;

    try {
      setCheckoutLoading(priceId);
      setErrorMessage("");

      // Create checkout session directly in Firestore
      const docRef = await addDoc(
        collection(db, "customers", user.uid, "checkout_sessions"),
        {
          price: priceId,
          success_url: `${window.location.origin}/dashboard`,
          cancel_url: `${window.location.origin}/subscribe-now`,
          allow_promotion_codes: true,

        }
      );

      // Wait for the CheckoutSession to get attached by the extension
      const unsubscribe = onSnapshot(docRef, (snap) => {
        const data = snap.data();
        if (data?.error) {
          // Show an error to your customer and
          // inspect your Cloud Function logs in the Firebase console.
          console.error("Checkout session error:", data.error);
          setErrorMessage(`An error occurred: ${data.error.message}`);
          setCheckoutLoading(null);
          unsubscribe();
        }
        if (data?.url) {
          // We have a Stripe Checkout URL, let's redirect.
          window.location.assign(data.url);
          unsubscribe();
        }
      });

    } catch (error) {
      console.error("Error creating checkout session:", error);
      setErrorMessage("Failed to start checkout. Please try again.");
      setCheckoutLoading(null);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "Contact Us";
    const amount = price.unit_amount / 100;
    const interval = price.recurring?.interval || "month";
    return `$${amount}/${interval}`;
  };

  const getPopularPlan = () => {
    // Mark the middle plan as popular, or the second plan if we have multiple
    if (products.length >= 2) {
      return Math.floor(products.length / 2);
    }
    return -1;
  };

  const listOfFeatures = [
    "Interactive fence designer",
	"Instant quotes",
	"Custom fence types and prices",
	"Admin dashboard"
  ]

  if (loading || loadingProducts) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-lg">Loading subscription plans...</Typography>
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          ":root": {
            "--Transition-duration": "0.4s",
          },
        }}
      />
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          py: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: 3,
          }}
        >
          {/* Header */}
          <Box
            component="header"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <img width={160} src={FenceLinkNameLogo} alt="FenceLink icon" />

            <Button
            onClick={() => signOut(auth)}
            >
              Sign out
            </Button>
          </Box>

          {/* Title Section */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              level="h1"
              sx={{
                fontSize: { xs: "2rem", md: "3rem" },
                fontWeight: "bold",
                color: "white",
                mb: 2,
              }}
            >
              Choose Your Plan
            </Typography>

            <Typography
              level="body-lg"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                maxWidth: "600px",
                marginBottom: "20px", 
                mx: "auto",
              }}
            >
              logged in as {user.email}
            </Typography>
            <Typography
              level="body-lg"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Complete your subscription to unlock powerful tools designed to win more fence jobs and simplify your workflow.
            </Typography>
          </Box>

          {/* Error Message */}
          {errorMessage && (
            <Box sx={{ mb: 4 }}>
              <Alert color="danger" variant="soft">
                {errorMessage}
              </Alert>
            </Box>
          )}

          {/* Pricing Cards */}
          {products.length > 0 ? (
            <Grid container spacing={3} sx={{ justifyContent: "center" }}>
              {products.map((product, index) => {
                const price = product.prices?.[0];
                const isPopular = index === getPopularPlan();
                const isLoading = checkoutLoading === price?.id;

                return (
                  <Grid xs={12} md={4} key={product.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        position: "relative",
                        transform: isPopular ? "scale(1.05)" : "none",
                        transition: "transform 0.2s ease-in-out",
                        border: isPopular ? "2px solid #4CAF50" : "1px solid rgba(255, 255, 255, 0.2)",
                        background: "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(10px)",
                        "&:hover": {
                          transform: isPopular ? "scale(1.08)" : "scale(1.02)",
                        },
                      }}
                    >
                      {isPopular && (
                        <Chip
                          color="success"
                          size="sm"
                          sx={{
                            position: "absolute",
                            top: -10,
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 1,
                          }}
                        >
                          Most Popular
                        </Chip>
                      )}
                      
                      <CardContent sx={{ p: 3, textAlign: "center", height: "100%" }}>
                        <Stack spacing={3} sx={{ height: "100%" }}>
                          {/* Plan Name */}
                          <Typography level="h3" sx={{ fontWeight: "bold" }}>
                            {product.name}
                          </Typography>

                          {/* Price */}
                          <Box>
                            <Typography
                              level="h1"
                              sx={{
                                fontSize: "2.5rem",
                                fontWeight: "bold",
                                color: "primary.main",
                              }}
                            >
                              {formatPrice(price)}
                            </Typography>
                          </Box>

                          {/* Description */}
                          {product.description && (
                            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                              {product.description}
                            </Typography>
                          )}

                          {/* Features */}
                          <Stack spacing={1} sx={{ flex: 1, justifyContent: "center" }}>
                            {product.metadata?.features ? (
                              JSON.parse(product.metadata.features).map((feature, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <CheckIcon sx={{ color: "success.main", fontSize: "1rem" }} />
                                  <Typography level="body-sm">{feature}</Typography>
                                </Box>
                              ))
                            ) : (
                                listOfFeatures.map(feature => {
                                    return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                            <CheckIcon sx={{ color: "success.main", fontSize: "1rem" }} />
                                            <Typography level="body-sm">{feature}</Typography>
                              </Box>
                                    )
                                })
                            )}
                          </Stack>

                          {/* Subscribe Button */}
                          <Button
                            size="lg"
                            variant={isPopular ? "solid" : "outlined"}
                            color={isPopular ? "success" : "primary"}
                            onClick={() => handleSubscribe(price?.id)}
                            loading={isLoading}
                            disabled={!price || isLoading}
                            sx={{
                              mt: "auto",
                              fontWeight: "bold",
                            }}
                          >
                            {isLoading ? "Processing..." : "Get Started"}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography level="h3" sx={{ color: "white", mb: 2 }}>
                No subscription plans available
              </Typography>
              <Typography level="body-lg" sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 4 }}>
                Please contact support or try again later.
              </Typography>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => navigate("/dashboard")}
                sx={{ color: "white", borderColor: "white" }}
              >
                Continue to Dashboard
              </Button>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ textAlign: "center", mt: 8, pt: 4, borderTop: "1px solid rgba(255, 255, 255, 0.2)" }}>
            <Typography level="body-sm" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
              © {settings.companyName} {new Date().getFullYear()} • Secure payments powered by Stripe
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
